/**
 * 顧客管理（Excel）画面 - Customer_Grid
 *
 * Jspreadsheet CE を使用してExcelのような操作性を実現する。
 * セル範囲選択、コピー&ペースト、直接編集、固定列1列（顧客コード）に対応。
 *
 * 実装はライセンス管理（Excel）画面（LicenseSpreadsheetPage.tsx）のロジックを
 * 顧客の8列構成に合わせて移植したもの。
 * 詳細な仕様は docs/excel-like-ui-spec.md を参照。
 */
import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
  Box, Typography, Button, Alert, CircularProgress, Menu, MenuItem as MuiMenuItem,
  ListItemIcon, ListItemText, Divider, TextField, InputAdornment, IconButton, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, FormControlLabel, FormGroup,
  GlobalStyles,
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
  VisibilityOff as VisibilityOffIcon,
  Visibility as VisibilityIcon,
  ViewColumn as ViewColumnIcon,
  Edit as EditIcon,
  AddCircle as AddCircleIcon,
  Delete as DeleteIcon2,
  WarningAmber as WarningIcon,
  ContentCopy as CopyIcon,
  ContentPaste as PasteIcon,
  ArrowBack as ArrowLeftIcon,
  ArrowForward as ArrowRightIcon,
  AddCircleOutline as InsertRowIcon,
  RemoveCircleOutline as DeleteRowIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  Undo as UndoIcon,
  Redo as RedoIcon,
  Comment as CommentIcon,
  Palette as PaletteIcon,
  FormatColorReset as ClearColorIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useNavigationGuard } from '../contexts/NavigationGuardContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import jspreadsheet from 'jspreadsheet-ce';
import 'jspreadsheet-ce/dist/jspreadsheet.css';
import 'jsuites/dist/jsuites.css';
import api from '../services/api';
import { useAuth } from '../hooks/useAuth';
import { useSnackbar } from '../components/AppSnackbar';
import {
  type CellMetaMap,
  CELL_COLOR_PALETTE,
  parseCellMeta,
  serializeCellMeta,
  cellNameFromCoords,
  extractBackgroundColor,
  displayToOriginalRow,
  normalizeColumnOrder,
  sanitizeHiddenColumns,
  saveColumnState,
  loadColumnState,
} from '../utils/customerCellMeta';

// ============================================================
// 列定義（Orig_Index 固定）
// ============================================================

interface ColumnDef {
  title: string;
  width: number;
}

const COLUMNS: ColumnDef[] = [
  { title: '顧客コード', width: 130 },  // 0 必須・固定列
  { title: '顧客名', width: 180 },       // 1
  { title: '顧客名カナ', width: 180 },   // 2
  { title: '担当者名', width: 130 },      // 3
  { title: '電話番号', width: 130 },      // 4
  { title: 'メールアドレス', width: 200 }, // 5
  { title: '住所', width: 240 },          // 6
  { title: '備考', width: 240 },          // 7
  { title: '資産', width: 70 },           // 8  リンクアイコン列（資産台帳）
  { title: '契約', width: 70 },           // 9  リンクアイコン列（契約管理）
  { title: 'ライセンス', width: 70 },     // 10 リンクアイコン列（ライセンス管理）
  { title: 'カルテ', width: 70 },         // 11 リンクアイコン列（顧客カルテ）
  { title: 'チケット', width: 70 },       // 12 リンクアイコン列（チケット管理）
];

/** リンクアイコン列の Orig_Index 一覧（保存対象外・cellMeta 対象外） */
const LINK_COLUMNS: number[] = [8, 9, 10, 11, 12];

/** データ列の数（保存対象は Orig_Index 0〜7 のみ） */
const DATA_COLUMN_COUNT = 8;

/** 常時左端に固定表示する列（Display_Order の先頭1位置に固定する） */
const FIXED_COLUMNS: number[] = [0];

/** 必須列（Orig_Index）。顧客コードのみ。 */
const REQUIRED_COLUMNS: number[] = [0];
const REQUIRED_HIGHLIGHT_COLOR = '#ed6c02';

/** 新規行のプレースホルダID接頭辞 */
const NEW_ROW_ID_PREFIX = '__new_';

function isNewRowId(rowId: string | null | undefined): boolean {
  return !rowId || rowId.startsWith(NEW_ROW_ID_PREFIX);
}

function isBlankValue(value: unknown): boolean {
  return String(value ?? '').trim() === '';
}

// ============================================================
// 列移動スナップバック（固定列を先頭に戻す）
// ============================================================
function snapBackOrder(order: number[], from: number, to: number): number[] {
  const next = [...order];
  if (from >= 0 && from < next.length) {
    const [moved] = next.splice(from, 1);
    const target = Math.max(0, Math.min(to, next.length));
    next.splice(target, 0, moved);
  }
  for (let fixedPos = 0; fixedPos < FIXED_COLUMNS.length; fixedPos++) {
    const fixedIdx = FIXED_COLUMNS[fixedPos];
    const current = next.indexOf(fixedIdx);
    if (current !== -1 && current !== fixedPos) {
      next.splice(current, 1);
      next.splice(fixedPos, 0, fixedIdx);
    }
  }
  return next;
}

// ============================================================
// 行ID操作
// ============================================================
type RowIdOperation =
  | { type: 'insert'; position: number; newIds: string[] }
  | { type: 'delete'; position: number; count: number }
  | { type: 'reorder'; order: number[] };

function applyRowIdOperation(ids: string[], op: RowIdOperation): string[] {
  switch (op.type) {
    case 'insert': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      return [...ids.slice(0, position), ...op.newIds, ...ids.slice(position)];
    }
    case 'delete': {
      const position = Math.max(0, Math.min(op.position, ids.length));
      const count = Math.max(0, Math.min(op.count, ids.length - position));
      return [...ids.slice(0, position), ...ids.slice(position + count)];
    }
    case 'reorder':
      return op.order.map((prevIdx) => ids[prevIdx]);
    default:
      return [...ids];
  }
}

// ============================================================
// dirty行の分類
// ============================================================
type SaveRequestKind = 'post' | 'put' | 'patch' | 'both' | 'skip';

interface RowSavePlan {
  index: number;
  rowId: string;
  kind: SaveRequestKind;
}

function classifyDirtyRows(
  rowIds: (string | null | undefined)[],
  dirtyContentIds: Iterable<string>,
  dirtyMetaIds: Iterable<string>,
): RowSavePlan[] {
  const contentSet = new Set(dirtyContentIds);
  const metaSet = new Set(dirtyMetaIds);
  return rowIds.map((rawId, index) => {
    const rowId = rawId ?? '';
    if (isNewRowId(rowId)) return { index, rowId, kind: 'post' as SaveRequestKind };
    const contentDirty = contentSet.has(rowId);
    const metaDirty = metaSet.has(rowId);
    let kind: SaveRequestKind = 'skip';
    if (contentDirty && metaDirty) kind = 'both';
    else if (contentDirty) kind = 'put';
    else if (metaDirty) kind = 'patch';
    return { index, rowId, kind };
  });
}

// ============================================================
// データ型
// ============================================================
interface CustomerDto {
  id: string;
  customerCode: string;
  name: string | null;
  nameKana: string | null;
  contactName: string | null;
  phone: string | null;
  email: string | null;
  address: string | null;
  notes: string | null;
  cellMeta?: string | null;
  assetCount?: number;
  contractCount?: number;
  licenseCount?: number;
}

type CellValue = string | number | null | undefined;

/** CustomerDto → Orig_Index 順の行配列 */
function customerToRow(c: CustomerDto): CellValue[] {
  return [
    c.customerCode || '',  // 0
    c.name || '',          // 1
    c.nameKana || '',      // 2
    c.contactName || '',   // 3
    c.phone || '',         // 4
    c.email || '',         // 5
    c.address || '',       // 6
    c.notes || '',         // 7
    (c.assetCount ?? 0) > 0 ? '●' : '',     // 8 資産リンク
    (c.contractCount ?? 0) > 0 ? '●' : '',   // 9 契約リンク
    (c.licenseCount ?? 0) > 0 ? '●' : '',    // 10 ライセンスリンク
    '●',                                      // 11 カルテリンク（全顧客で常時表示）
    '●',                                      // 12 チケットリンク（全顧客で常時表示）
  ];
}

// ============================================================
// Component
// ============================================================

const CustomerSpreadsheetPage: React.FC = () => {
  const { user } = useAuth();
  const { showSuccess, showError } = useSnackbar();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const jRef = useRef<HTMLDivElement>(null);
  const spreadsheetRef = useRef<any>(null);
  const isAdmin = user?.role === 'admin';
  const userId = user?.id || 'anonymous';

  // Row index → Customer_Record id
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [customerIds, setCustomerIds] = useState<string[]>([]);
  const customerIdsRef = useRef<string[]>([]);
  const pendingDeleteIdsRef = useRef<Set<string>>(new Set());
  const dirtyContentRowIdsRef = useRef<Set<string>>(new Set());
  const dirtyMetaRowIdsRef = useRef<Set<string>>(new Set());

  const [dirtyCounts, setDirtyCounts] = useState<{ modified: number; added: number; deleted: number }>({ modified: 0, added: 0, deleted: 0 });
  const isDirty = dirtyCounts.modified > 0 || dirtyCounts.added > 0 || dirtyCounts.deleted > 0;
  const deletedBatchStackRef = useRef<string[][]>([]);
  const [searchText, setSearchText] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Active ticket session customer IDs (for highlighting ticket column)
  const activeTicketCustomerIdsRef = useRef<Set<string>>(new Set());
  const { data: activeTicketData } = useQuery<{ customerIds: string[] }>({
    queryKey: ['ticket-active-customers'],
    queryFn: async () => { const res = await api.get('/tickets/active-customers'); return res.data; },
    refetchInterval: 30000, // refresh every 30 seconds
  });
  useEffect(() => {
    activeTicketCustomerIdsRef.current = new Set(activeTicketData?.customerIds ?? []);
    // Re-apply link cell styles to update highlight
    if (spreadsheetRef.current) {
      applyTicketHighlight();
    }
  }, [activeTicketData]);
  const columnFilterSelectionsRef = useRef<Map<number, Set<string>>>(new Map());
  /** Color filter: origCol → set of selected colors (hex). Special value '__none__' = no color. */
  const colorFilterSelectionsRef = useRef<Map<number, Set<string>>>(new Map());

  const [hiddenColumns, setHiddenColumns] = useState<Set<number>>(() => {
    const saved = loadColumnState(userId);
    return sanitizeHiddenColumns(saved?.hidden, FIXED_COLUMNS);
  });
  const hiddenColumnsRef = useRef<Set<number>>(hiddenColumns);
  hiddenColumnsRef.current = hiddenColumns;
  const [columnVisibilityDialogOpen, setColumnVisibilityDialogOpen] = useState(false);
  const [filterDialog, setFilterDialog] = useState<{
    origCol: number;
    title: string;
    allValues: string[];
    selected: Set<string>;
    search: string;
    allColors: string[];
    selectedColors: Set<string>;
  } | null>(null);
  const [contextMenu, setContextMenu] = useState<{
    mouseX: number; mouseY: number; col: number; row: number;
    hasRangeSelection: boolean;
    range: [number, number, number, number];
    isHeader?: boolean;
  } | null>(null);

  const [statusBar, setStatusBar] = useState<{
    count: number; numericCount: number; sum: number; avg: number; min: number; max: number;
  } | null>(null);
  const statusBarRafRef = useRef<number | null>(null);

  const scheduleStatusBarUpdate = useCallback((_inst: any, x1: number, y1: number, x2: number, y2: number) => {
    if (statusBarRafRef.current !== null) cancelAnimationFrame(statusBarRafRef.current);
    statusBarRafRef.current = requestAnimationFrame(() => {
      statusBarRafRef.current = null;
      try {
        const inst = spreadsheetRef.current as any;
        if (!inst) return;
        const colStart = Math.min(x1, x2);
        const colEnd = Math.max(x1, x2);
        const rowStart = Math.min(y1, y2);
        const rowEnd = Math.max(y1, y2);
        const count = (colEnd - colStart + 1) * (rowEnd - rowStart + 1);
        let numericCount = 0; let sum = 0; let min = Infinity; let max = -Infinity;
        for (let r = rowStart; r <= rowEnd; r++) {
          for (let c = colStart; c <= colEnd; c++) {
            const raw = inst.getValueFromCoords(c, r);
            const str = String(raw ?? '').replace(/,/g, '').trim();
            if (str === '') continue;
            const n = Number(str);
            if (!Number.isNaN(n)) { numericCount++; sum += n; if (n < min) min = n; if (n > max) max = n; }
          }
        }
        if (numericCount === 0) setStatusBar({ count, numericCount: 0, sum: 0, avg: 0, min: 0, max: 0 });
        else setStatusBar({ count, numericCount, sum, avg: sum / numericCount, min, max });
      } catch { /* ignore */ }
    });
  }, []);

  const restoreContextSelection = useCallback(() => {
    if (!contextMenu) return;
    const [x1, y1, x2, y2] = contextMenu.range;
    spreadsheetRef.current?.updateSelectionFromCoords(x1, y1, x2, y2);
  }, [contextMenu]);

  const columnOrderRef = useRef<number[]>([]);
  const columnMoveSnapBackRef = useRef(false);
  // 直前に「右クリック→コピー」でクリップボードへ書き込んだテキスト。貼り付け時に
  // 一致すれば同一グリッド内コピーと判断し、jspreadsheet の内部コピーデータを
  // paste に渡して色も復元する（範囲コピーでも色が付く）。
  const lastCopiedTextRef = useRef<string | null>(null);
  const [commentDialog, setCommentDialog] = useState<{ col: number; row: number; value: string } | null>(null);

  const updateCustomerIds = useCallback((updater: (prev: string[]) => string[]) => {
    setCustomerIds((prev) => {
      const next = updater(prev);
      customerIdsRef.current = next;
      return next;
    });
  }, []);

  const markRowContentDirtyByIndex = useCallback((rowIndex: number) => {
    const id = customerIdsRef.current[rowIndex];
    if (id && !isNewRowId(id)) dirtyContentRowIdsRef.current.add(id);
  }, []);

  const markRowMetaDirtyByIndex = useCallback((rowIndex: number) => {
    const id = customerIdsRef.current[rowIndex];
    if (id && !isNewRowId(id)) dirtyMetaRowIdsRef.current.add(id);
  }, []);

  const updateDirtyCounts = useCallback(() => {
    const modified = dirtyContentRowIdsRef.current.size + dirtyMetaRowIdsRef.current.size;
    const added = customerIdsRef.current.filter((id) => id.startsWith(NEW_ROW_ID_PREFIX)).length;
    const deleted = pendingDeleteIdsRef.current.size;
    setDirtyCounts({ modified, added, deleted });
  }, []);

  const restoreDeletedBatch = useCallback((entry: any) => {
    const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
    const count = entry.numOfRows || 1;
    const restoredIds = deletedBatchStackRef.current.pop()
      ?? Array.from({ length: count }, (_, i) => `${NEW_ROW_ID_PREFIX}${Date.now()}_${i}`);
    restoredIds.forEach((id) => pendingDeleteIdsRef.current.delete(id));
    const next = applyRowIdOperation(customerIdsRef.current, { type: 'insert', position, newIds: restoredIds });
    customerIdsRef.current = next;
    setCustomerIds(next);
  }, []);

  const removeRowBatch = useCallback((entry: any, isDeleteAction: boolean) => {
    const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
    const count = entry.numOfRows || 1;
    const removedIds = customerIdsRef.current.slice(position, position + count);
    if (isDeleteAction) {
      removedIds.filter((id) => id && !isNewRowId(id)).forEach((id) => pendingDeleteIdsRef.current.add(id));
      deletedBatchStackRef.current.push(removedIds);
    } else {
      removedIds.forEach((id) => pendingDeleteIdsRef.current.delete(id));
    }
    const next = applyRowIdOperation(customerIdsRef.current, { type: 'delete', position, count });
    customerIdsRef.current = next;
    setCustomerIds(next);
  }, []);

  // Fetch customers
  const { data, isLoading, error } = useQuery<{ data: CustomerDto[] }>({
    queryKey: ['customers-spreadsheet'],
    queryFn: async () => { const res = await api.get('/customers'); return res.data; },
  });

  // Freeze offsets (1 fixed column only)
  const updateFreezeOffsets = useCallback(() => {
    const container = jRef.current;
    if (!container) return;
    const firstDataRow = container.querySelector('tbody tr');
    if (!firstDataRow) return;
    const rowNumCell = firstDataRow.querySelector('td.jexcel_row') as HTMLElement | null;
    const col0Width = rowNumCell?.offsetWidth ?? 0;
    container.style.setProperty('--freeze-offset-col0', `${col0Width}px`);
  }, []);

  const saveColumnStateToStorage = useCallback(() => {
    if (!spreadsheetRef.current) return;
    const widths: number[] = [];
    for (let i = 0; i < COLUMNS.length; i++) {
      widths.push(spreadsheetRef.current.getWidth(i) || COLUMNS[i].width);
    }
    const order = columnOrderRef.current.length === COLUMNS.length ? [...columnOrderRef.current] : COLUMNS.map((_, i) => i);
    const hidden = Array.from(hiddenColumnsRef.current);
    saveColumnState(userId, { widths, order, hidden });
  }, [userId]);

  // Column visibility helpers
  const applyColumnVisibility = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const currentOrder = columnOrderRef.current;
    const hidden = hiddenColumnsRef.current;
    for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
      const origIdx = currentOrder[displayIdx];
      if (hidden.has(origIdx)) inst.hideColumn(displayIdx);
      else inst.showColumn(displayIdx);
    }
    for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
      const headerCell = inst.headers?.[displayIdx] as HTMLElement | undefined;
      if (!headerCell) continue;
      const prevOrig = displayIdx > 0 ? currentOrder[displayIdx - 1] : undefined;
      const prevIsHidden = prevOrig !== undefined && hidden.has(prevOrig);
      const selfHidden = hidden.has(currentOrder[displayIdx]);
      headerCell.classList.toggle('hidden-col-boundary', prevIsHidden && !selfHidden);
    }
  }, []);

  const hideColumnsByOrigIdx = useCallback((origIdxs: number[]) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      origIdxs.filter((i) => !FIXED_COLUMNS.includes(i)).forEach((i) => next.add(i));
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  const showColumnsByOrigIdx = useCallback((origIdxs: number[]) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      origIdxs.forEach((i) => next.delete(i));
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  const toggleColumnVisibility = useCallback((origIdx: number) => {
    if (FIXED_COLUMNS.includes(origIdx)) return;
    setHiddenColumns((prev) => {
      const next = new Set(prev);
      if (next.has(origIdx)) next.delete(origIdx); else next.add(origIdx);
      hiddenColumnsRef.current = next;
      return next;
    });
  }, []);

  const showAllColumns = useCallback(() => {
    setHiddenColumns(() => { const next = new Set<number>(); hiddenColumnsRef.current = next; return next; });
  }, []);

  useEffect(() => {
    applyColumnVisibility();
    saveColumnStateToStorage();
  }, [hiddenColumns, applyColumnVisibility, saveColumnStateToStorage]);

  const getAdjacentHiddenOrigIdxs = useCallback((displayIdx: number): number[] => {
    const currentOrder = columnOrderRef.current;
    const hidden = hiddenColumnsRef.current;
    const result: number[] = [];
    for (let i = displayIdx - 1; i >= 0; i--) { const orig = currentOrder[i]; if (hidden.has(orig)) result.push(orig); else break; }
    for (let i = displayIdx + 1; i < currentOrder.length; i++) { const orig = currentOrder[i]; if (hidden.has(orig)) result.push(orig); else break; }
    return result;
  }, []);

  const applyColumnFilters = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const currentOrder = columnOrderRef.current;
    const activeFilters = Array.from(columnFilterSelectionsRef.current.entries());
    const activeColorFilters = Array.from(colorFilterSelectionsRef.current.entries());
    const rowCount = customerIdsRef.current.length;
    const styleMap = (inst as any).options?.style ?? (inst as any).style;
    if (activeFilters.length === 0 && activeColorFilters.length === 0) {
      for (let row = 0; row < rowCount; row++) {
        const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') inst.showRow(row);
      }
    } else {
      for (let row = 0; row < rowCount; row++) {
        let matches = true;
        for (const [origCol, selectedValues] of activeFilters) {
          const displayCol = currentOrder.indexOf(origCol);
          if (displayCol === -1) continue;
          const cellValue = String(inst.getValueFromCoords(displayCol, row) ?? '').trim();
          if (!selectedValues.has(cellValue)) { matches = false; break; }
        }
        if (matches) {
          for (const [origCol, selectedColors] of activeColorFilters) {
            const displayCol = currentOrder.indexOf(origCol);
            if (displayCol === -1) continue;
            const cellName = cellNameFromCoords(displayCol, row);
            const styleStr = styleMap?.[cellName] as string | undefined;
            const colorMatch = styleStr ? /background-color\s*:\s*([^;]+)/i.exec(styleStr) : null;
            const cellColor = colorMatch ? colorMatch[1].trim().toLowerCase() : '__none__';
            if (!selectedColors.has(cellColor)) {
              matches = false;
              break;
            }
          }
        }
        if (matches) {
          const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
          if (tr && tr.style.display === 'none') inst.showRow(row);
        } else {
          inst.hideRow(row);
        }
      }
    }
    // Re-apply cell colors
    if (styleMap) {
      for (let row = 0; row < rowCount; row++) {
        const tr = (inst as any).rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') continue;
        for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
          const cellName = cellNameFromCoords(displayIdx, row);
          const color = extractBackgroundColor(styleMap[cellName] as string | undefined);
          if (!color) continue;
          const cell = inst.getCellFromCoords(displayIdx, row) as HTMLElement | null;
          if (cell) cell.style.backgroundColor = color;
        }
      }
    }
  }, []);

  const isColumnFiltered = useCallback(
    (origCol: number) => columnFilterSelectionsRef.current.has(origCol) || colorFilterSelectionsRef.current.has(origCol), [],
  );

  const updateFilterIconIndicators = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst?.headers) return;
    columnOrderRef.current.forEach((origCol: number, displayCol: number) => {
      const headerCell = inst.headers[displayCol] as HTMLElement | undefined;
      if (!headerCell) return;
      headerCell.classList.toggle('column-filter-active', isColumnFiltered(origCol));
    });
  }, [isColumnFiltered]);

  const openFilterDialog = useCallback((displayCol: number) => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const origCol = columnOrderRef.current[displayCol];
    if (origCol === undefined) return;
    const rowCount = customerIdsRef.current.length;
    const uniqueValues = new Set<string>();
    const uniqueColors = new Set<string>();
    let hasNoColor = false;
    const styleMap = (inst as any).options?.style ?? (inst as any).style;
    for (let row = 0; row < rowCount; row++) {
      uniqueValues.add(String(inst.getValueFromCoords(displayCol, row) ?? '').trim());
      const cellName = cellNameFromCoords(displayCol, row);
      const styleStr = styleMap?.[cellName] as string | undefined;
      const colorMatch = styleStr ? /background-color\s*:\s*([^;]+)/i.exec(styleStr) : null;
      if (colorMatch) {
        uniqueColors.add(colorMatch[1].trim().toLowerCase());
      } else {
        hasNoColor = true;
      }
    }
    const allValues = Array.from(uniqueValues).sort((a, b) => a.localeCompare(b, 'ja'));
    const existingSelection = columnFilterSelectionsRef.current.get(origCol);
    const selected = existingSelection ? new Set(existingSelection) : new Set(allValues);

    const allColors: string[] = [];
    if (hasNoColor) allColors.push('__none__');
    uniqueColors.forEach((c) => allColors.push(c));

    const existingColorSelection = colorFilterSelectionsRef.current.get(origCol);
    const selectedColors = existingColorSelection ? new Set(existingColorSelection) : new Set(allColors);

    setFilterDialog({ origCol, title: COLUMNS[origCol]?.title ?? '', allValues, selected, search: '', allColors, selectedColors });
  }, []);

  const handleFilterDialogToggleValue = (value: string) => {
    setFilterDialog((prev) => { if (!prev) return prev; const next = new Set(prev.selected); if (next.has(value)) next.delete(value); else next.add(value); return { ...prev, selected: next }; });
  };

  const getVisibleFilterValues = (dialog: { allValues: string[]; search: string }): string[] => {
    const term = dialog.search.trim().toLowerCase();
    if (!term) return dialog.allValues;
    return dialog.allValues.filter((v) => v.toLowerCase().includes(term));
  };

  const handleFilterDialogSelectAll = () => {
    setFilterDialog((prev) => { if (!prev) return prev; const visible = getVisibleFilterValues(prev); return { ...prev, selected: new Set(visible) }; });
  };

  const handleFilterDialogDeselectAll = () => {
    setFilterDialog((prev) => { if (!prev) return prev; const visible = getVisibleFilterValues(prev); const next = new Set(prev.selected); visible.forEach((v) => next.delete(v)); return { ...prev, selected: next }; });
  };

  const handleFilterDialogSearchChange = (newSearch: string) => {
    setFilterDialog((prev) => { if (!prev) return prev; const term = newSearch.trim().toLowerCase(); const visible = term ? prev.allValues.filter((v) => v.toLowerCase().includes(term)) : prev.allValues; return { ...prev, search: newSearch, selected: new Set(visible) }; });
  };

  const handleFilterDialogOk = () => {
    if (!filterDialog) return;
    const { origCol, selected, allValues, selectedColors, allColors } = filterDialog;
    if (selected.size === allValues.length) columnFilterSelectionsRef.current.delete(origCol);
    else columnFilterSelectionsRef.current.set(origCol, new Set(selected));
    if (selectedColors.size === allColors.length) {
      colorFilterSelectionsRef.current.delete(origCol);
    } else {
      colorFilterSelectionsRef.current.set(origCol, new Set(selectedColors));
    }
    setFilterDialog(null);
    applyColumnFilters();
    updateFilterIconIndicators();
    requestAnimationFrame(() => {
      const inst = spreadsheetRef.current as any;
      if (!inst) return;
      const currentOrder = columnOrderRef.current;
      const rowCount = customerIdsRef.current.length;
      const styleMap = inst.options?.style ?? inst.style;
      if (!styleMap) return;
      for (let row = 0; row < rowCount; row++) {
        const tr = inst.rows?.[row] as HTMLElement | undefined;
        if (tr && tr.style.display === 'none') continue;
        for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
          const cellName = cellNameFromCoords(displayIdx, row);
          const color = extractBackgroundColor(styleMap[cellName] as string | undefined);
          if (!color) continue;
          const cell = inst.getCellFromCoords(displayIdx, row) as HTMLElement | null;
          if (cell) cell.style.backgroundColor = color;
        }
      }
    });
  };

  const handleFilterDialogCancel = () => { setFilterDialog(null); };

  useEffect(() => { columnFilterSelectionsRef.current.clear(); colorFilterSelectionsRef.current.clear(); }, [data]);

  // 必須項目の未入力強調
  const applyRequiredFieldHighlight = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const order = columnOrderRef.current;
    const requiredDisplayIdxs = new Set(
      REQUIRED_COLUMNS.map((origIdx) => order.indexOf(origIdx)).filter((displayIdx) => displayIdx !== -1),
    );
    const rowCount = (inst.getData() as unknown[][]).length;
    // 全列を走査してから必須列にのみ枠を当てる。列を並べ替えるとセル要素が表示位置
    // 間を移動するため、必須列だけをループすると「並べ替え前の表示位置」に付けた
    // outline がそこに来た別の列に残り、必須でない列にオレンジ枠が出てしまう。
    for (let r = 0; r < rowCount; r++) {
      for (let displayIdx = 0; displayIdx < order.length; displayIdx++) {
        const cell = inst.getCellFromCoords(displayIdx, r) as HTMLElement | null;
        if (!cell) continue;
        const blank =
          requiredDisplayIdxs.has(displayIdx) && isBlankValue(inst.getValueFromCoords(displayIdx, r));
        cell.style.outline = blank ? `2px solid ${REQUIRED_HIGHLIGHT_COLOR}` : '';
        cell.style.outlineOffset = blank ? '-2px' : '';
      }
    }
  }, []);

  // リンクアイコン列のセルにクラスとカラーを適用
  // 各列に固有の色を付けて視認性を高める（絵文字はOS依存で薄く見えるため廃止し、
  // テキスト●＋色で代替）。
  const LINK_COLUMN_COLORS: Record<number, string> = {
    8: '#1976d2',  // 資産 — ブルー
    9: '#2e7d32',  // 契約 — グリーン
    10: '#ed6c02', // ライセンス — オレンジ
    11: '#7b1fa2', // カルテ — パープル
    12: '#d32f2f', // チケット — レッド
  };

  const applyLinkCellStyles = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const order = columnOrderRef.current;
    // 表示位置 → リンク列の Orig_Index（リンク列でない表示位置は undefined）。
    const origByDisplayIdx = new Map<number, number>();
    LINK_COLUMNS.forEach((origIdx) => {
      const displayIdx = order.indexOf(origIdx);
      if (displayIdx !== -1) origByDisplayIdx.set(displayIdx, origIdx);
    });
    const rowCount = (inst.getData() as unknown[][]).length;
    // 必須枠と同様に全列を走査する。リンク列だけをループすると、列を並べ替えたときに
    // 「並べ替え前の表示位置」に付けた色・太字・クラスがそこに来た別の列に残ってしまう。
    for (let r = 0; r < rowCount; r++) {
      for (let displayIdx = 0; displayIdx < order.length; displayIdx++) {
        const cell = inst.getCellFromCoords(displayIdx, r) as HTMLElement | null;
        if (!cell) continue;
        const origIdx = origByDisplayIdx.get(displayIdx);
        const val = origIdx === undefined
          ? ''
          : String(inst.getValueFromCoords(displayIdx, r) ?? '').trim();
        if (origIdx !== undefined && val) {
          cell.classList.add('customer-link-cell');
          cell.style.color = LINK_COLUMN_COLORS[origIdx] || '#1976d2';
          cell.style.fontWeight = 'bold';
          cell.style.fontSize = '16px';
        } else {
          cell.classList.remove('customer-link-cell');
          cell.style.color = '';
          cell.style.fontWeight = '';
          cell.style.fontSize = '';
        }
      }
    }
    // Apply ticket highlight after link styles
    applyTicketHighlight();
  }, []);

  /** Highlight ticket column cells (origIdx 12) when customer has an active session */
  const applyTicketHighlight = useCallback(() => {
    const inst = spreadsheetRef.current;
    if (!inst) return;
    const order = columnOrderRef.current;
    const ticketDisplayIdx = order.indexOf(12);
    if (ticketDisplayIdx === -1) return;
    const rowCount = (inst.getData() as unknown[][]).length;
    const activeIds = activeTicketCustomerIdsRef.current;
    for (let r = 0; r < rowCount; r++) {
      const cell = inst.getCellFromCoords(ticketDisplayIdx, r) as HTMLElement | null;
      if (!cell) continue;
      const custId = customerIdsRef.current[r];
      if (custId && activeIds.has(custId)) {
        cell.style.backgroundColor = '#ffcdd2'; // light red
      } else {
        cell.style.backgroundColor = '';
      }
    }
  }, []);

  // ============================================================
  // Initialize / rebuild the spreadsheet
  // ============================================================
  useEffect(() => {
    if (!jRef.current || !data?.data) return;

    if (spreadsheetRef.current) {
      try { jspreadsheet.destroy(jRef.current as any); } catch { /* ignore */ }
      spreadsheetRef.current = null;
    }

    const customers = data.data;
    const ids = customers.map((c) => c.id);
    setCustomerIds(ids);
    customerIdsRef.current = ids;

    const rowData = customers.map(customerToRow);

    const savedState = loadColumnState(userId);
    const rawOrder = savedState?.order && savedState.order.length === COLUMNS.length
      ? savedState.order : COLUMNS.map((_, i) => i);
    const savedOrder = normalizeColumnOrder(rawOrder, FIXED_COLUMNS);
    columnOrderRef.current = [...savedOrder];

    const orderedRowData = rowData.map((row) => savedOrder.map((origIdx) => row[origIdx]));

    // Build style init (cell background colors)
    const styleInit: Record<string, string> = {};
    customers.forEach((customer, rowIdx) => {
      const meta = parseCellMeta(customer.cellMeta);
      Object.entries(meta).forEach(([origColStr, entry]) => {
        const origCol = Number(origColStr);
        const displayCol = savedOrder.indexOf(origCol);
        if (displayCol === -1 || !entry.color) return;
        styleInit[cellNameFromCoords(displayCol, rowIdx)] = `background-color:${entry.color}`;
      });
    });

    // Build columns config - all text type, left aligned
    const columnsConfig = savedOrder.map((origIdx, displayIdx) => {
      const col = COLUMNS[origIdx];
      const isLinkCol = LINK_COLUMNS.includes(origIdx);
      return {
        title: col?.title ?? '',
        width: savedState?.widths?.[displayIdx] ?? col?.width ?? 100,
        readOnly: isLinkCol ? true : !isAdmin,
        align: isLinkCol ? 'center' : 'left',
      };
    });

    spreadsheetRef.current = jspreadsheet(jRef.current as any, {
      data: orderedRowData.length > 0 ? orderedRowData : [Array(COLUMNS.length).fill('')],
      columns: columnsConfig as any,
      defaultColWidth: 100,
      tableOverflow: true,
      tableHeight: '100%',
      columnSorting: true,
      columnDrag: true,
      filters: false,
      allowInsertRow: isAdmin,
      allowDeleteRow: isAdmin,
      allowInsertColumn: false,
      allowDeleteColumn: false,
      selectionCopy: true,
      copyCompatibility: true,
      search: false,
      pagination: 0,
      allowComments: true,
      style: styleInit,
      onchange: (_instance: any, _cell: any, _x: any, y: any) => {
        markRowContentDirtyByIndex(Number(y));
        updateUndoRedoButtons();
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      onresizecolumn: () => { saveColumnStateToStorage(); updateFreezeOffsets(); },
      // NOTE on the first callback argument: jspreadsheet-ce dispatches events
      // as `obj.dispatch('onmovecolumn', el, from, to)`, so the first argument
      // is the grid's CONTAINER DOM ELEMENT — NOT the spreadsheet instance.
      // Calling `instance.moveColumn(...)` on it throws a TypeError, which
      // aborted the rest of this handler (column-state persistence, freeze
      // offsets, the required-field outline and the link-cell styling) and
      // left columnMoveSnapBackRef stuck at `true`. Always go through
      // spreadsheetRef.current instead, and never let a throw skip the
      // bookkeeping below.
      onmovecolumn: (_el: any, from: number, to: number) => {
        const isSnapBack = columnMoveSnapBackRef.current;
        if (isSnapBack) {
          const order = columnOrderRef.current;
          const [moved] = order.splice(from, 1);
          order.splice(to, 0, moved);
        } else {
          const before = [...columnOrderRef.current];
          const afterMove = [...before];
          const [moved] = afterMove.splice(from, 1);
          afterMove.splice(to, 0, moved);
          columnOrderRef.current = afterMove;
          const target = snapBackOrder(before, from, to);
          const inst = spreadsheetRef.current;
          for (let fixedPos = 0; fixedPos < FIXED_COLUMNS.length; fixedPos++) {
            const fixedIdx = FIXED_COLUMNS[fixedPos];
            const current = columnOrderRef.current.indexOf(fixedIdx);
            if (!inst || current === -1 || current === fixedPos) continue;
            columnMoveSnapBackRef.current = true;
            try {
              inst.moveColumn(current, fixedPos);
            } finally {
              // Always clear the guard, even if moveColumn throws — otherwise
              // every later column move is mistaken for a snap-back and the
              // frozen columns stop being enforced entirely.
              columnMoveSnapBackRef.current = false;
            }
          }
          columnOrderRef.current = target;
        }
        saveColumnStateToStorage();
        updateFreezeOffsets();
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      onundo: (_instance: any, entry: any) => {
        if (entry?.action === 'insertRow') removeRowBatch(entry, false);
        else if (entry?.action === 'deleteRow') restoreDeletedBatch(entry);
        else if (entry?.action === 'orderBy' && Array.isArray(entry.rows)) {
          const order: number[] = entry.rows;
          const inverse: number[] = [];
          order.forEach((prevIdx, newIdx) => { inverse[prevIdx] = newIdx; });
          const next = applyRowIdOperation(customerIdsRef.current, { type: 'reorder', order: inverse });
          customerIdsRef.current = next;
          setCustomerIds(next);
        }
        if (entry?.action === 'setValue' && Array.isArray(entry.records) && entry.records.length > 0) {
          const first = entry.records[0]; const last = entry.records[entry.records.length - 1];
          const inst = spreadsheetRef.current;
          if (inst && first && last) inst.updateSelectionFromCoords(first.x, first.y, last.x, last.y);
        }
        updateUndoRedoButtons();
        // Undoing a deleteRow (or the insertRow-redo below) brings back rows
        // whose cells start fully visible — re-apply the hidden-column set so
        // they line up with every other row (see handleAddRow for details).
        applyColumnVisibility();
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      onredo: (_instance: any, entry: any) => {
        if (entry?.action === 'orderBy' && Array.isArray(entry.rows)) {
          const next = applyRowIdOperation(customerIdsRef.current, { type: 'reorder', order: entry.rows });
          customerIdsRef.current = next;
          setCustomerIds(next);
        } else if (entry?.action === 'insertRow') {
          const position = entry.insertBefore ? entry.rowNumber : entry.rowNumber + 1;
          const count = entry.numOfRows || 1;
          const placeholders = Array.from({ length: count }, (_, i) => `${NEW_ROW_ID_PREFIX}${Date.now()}_${i}`);
          const next = applyRowIdOperation(customerIdsRef.current, { type: 'insert', position, newIds: placeholders });
          customerIdsRef.current = next;
          setCustomerIds(next);
        } else if (entry?.action === 'deleteRow') {
          removeRowBatch(entry, true);
        }
        if (entry?.action === 'setValue' && Array.isArray(entry.records) && entry.records.length > 0) {
          const first = entry.records[0]; const last = entry.records[entry.records.length - 1];
          const inst = spreadsheetRef.current;
          if (inst && first && last) inst.updateSelectionFromCoords(first.x, first.y, last.x, last.y);
        }
        updateUndoRedoButtons();
        applyColumnVisibility();
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      ondeleterow: (_instance: any, rowNumber: number, numOfRows: number) => {
        const removedIds = customerIdsRef.current.slice(rowNumber, rowNumber + numOfRows);
        deletedBatchStackRef.current.push(removedIds);
        removedIds.filter((id) => id && !isNewRowId(id)).forEach((id) => pendingDeleteIdsRef.current.add(id));
        const remainingIds = applyRowIdOperation(customerIdsRef.current, { type: 'delete', position: rowNumber, count: numOfRows });
        customerIdsRef.current = remainingIds;
        setCustomerIds(remainingIds);
        updateUndoRedoButtons();
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      contextMenu: () => [] as object[],
      onsort: () => {
        try {
          const inst = spreadsheetRef.current as any;
          const history = inst?.history;
          const historyIndex = inst?.historyIndex;
          const entry = Array.isArray(history) && historyIndex >= 0 ? history[historyIndex] : null;
          if (entry && entry.action === 'orderBy' && Array.isArray(entry.rows)) {
            const next = applyRowIdOperation(customerIdsRef.current, { type: 'reorder', order: entry.rows });
            customerIdsRef.current = next;
            setCustomerIds(next);
          }
        } catch { /* ignore */ }
        applyRequiredFieldHighlight();
        applyLinkCellStyles();
      },
      onselection: (inst: any, x1: number, y1: number, x2: number, y2: number) => {
        try { scheduleStatusBarUpdate(inst, x1, y1, x2, y2); } catch { /* ignore */ }
      },
    });

    // Apply persisted comments
    customers.forEach((customer, rowIdx) => {
      const meta = parseCellMeta(customer.cellMeta);
      Object.entries(meta).forEach(([origColStr, entry]) => {
        const origCol = Number(origColStr);
        const displayCol = savedOrder.indexOf(origCol);
        if (displayCol === -1 || !entry.comment) return;
        spreadsheetRef.current?.setComments(cellNameFromCoords(displayCol, rowIdx), entry.comment, '');
      });
    });

    updateFreezeOffsets();
    const contentEl = jRef.current?.querySelector('.jexcel_content') as HTMLElement | null;
    contentEl?.addEventListener('scroll', updateFreezeOffsets);
    window.addEventListener('resize', updateFreezeOffsets);

    // Suppress browser's "Save password?" dialog
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node instanceof HTMLElement) {
            if (node.tagName === 'INPUT') node.setAttribute('autocomplete', 'off');
            node.querySelectorAll?.('input')?.forEach((input) => input.setAttribute('autocomplete', 'off'));
          }
        });
      }
    });
    if (jRef.current) observer.observe(jRef.current, { childList: true, subtree: true });
    applyColumnFilters();
    updateFilterIconIndicators();
    updateUndoRedoButtons();
    applyColumnVisibility();
    applyRequiredFieldHighlight();
    applyLinkCellStyles();

    return () => {
      contentEl?.removeEventListener('scroll', updateFreezeOffsets);
      window.removeEventListener('resize', updateFreezeOffsets);
      observer.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, isAdmin, userId, saveColumnStateToStorage, restoreDeletedBatch, removeRowBatch, updateFreezeOffsets, applyColumnFilters, updateFilterIconIndicators, markRowContentDirtyByIndex]);

  // Auto-scroll when dragging
  useEffect(() => {
    const container = jRef.current?.querySelector('.jexcel_content') as HTMLElement | null;
    if (!container) return;
    const EDGE_ZONE = 40; const MIN_SPEED = 2; const MAX_SPEED = 24;
    let isDragging = false; let animFrameId: number | null = null; let mouseX = 0; let mouseY = 0;
    function getSpeed(distance: number): number { const t = Math.min(distance / 80, 1); return MIN_SPEED + (MAX_SPEED - MIN_SPEED) * t * t; }
    function extendSelectionToPoint(clientX: number, clientY: number) {
      const inst = spreadsheetRef.current; if (!inst) return;
      const el = document.elementFromPoint(clientX, clientY); if (!el) return;
      const td = (el as HTMLElement).closest('td[data-x]') as HTMLElement | null; if (!td) return;
      const x = Number(td.getAttribute('data-x')); const y = Number(td.getAttribute('data-y'));
      if (Number.isNaN(x) || Number.isNaN(y)) return;
      const sel = (inst as any).selectedCell; if (sel) inst.updateSelectionFromCoords(sel[0], sel[1], x, y);
    }
    function scrollLoop() {
      if (!isDragging) return;
      const rect = container!.getBoundingClientRect();
      let dx = 0; let dy = 0;
      if (mouseX < rect.left + EDGE_ZONE) dx = -getSpeed(rect.left + EDGE_ZONE - mouseX);
      else if (mouseX > rect.right - EDGE_ZONE) dx = getSpeed(mouseX - (rect.right - EDGE_ZONE));
      if (mouseY < rect.top + EDGE_ZONE) dy = -getSpeed(rect.top + EDGE_ZONE - mouseY);
      else if (mouseY > rect.bottom - EDGE_ZONE) dy = getSpeed(mouseY - (rect.bottom - EDGE_ZONE));
      if (dx !== 0 || dy !== 0) { container!.scrollLeft += dx; container!.scrollTop += dy; extendSelectionToPoint(mouseX, mouseY); }
      animFrameId = requestAnimationFrame(scrollLoop);
    }
    function onMouseDown(e: MouseEvent) { if (e.button !== 0) return; if (!(e.target as HTMLElement).closest('.jexcel tbody')) return; isDragging = true; mouseX = e.clientX; mouseY = e.clientY; if (!animFrameId) animFrameId = requestAnimationFrame(scrollLoop); }
    function onMouseMove(e: MouseEvent) { if (!isDragging) return; mouseX = e.clientX; mouseY = e.clientY; if (!animFrameId) animFrameId = requestAnimationFrame(scrollLoop); }
    function onMouseUp() { isDragging = false; if (animFrameId) { cancelAnimationFrame(animFrameId); animFrameId = null; } }
    window.addEventListener('mousedown', onMouseDown, true);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => { window.removeEventListener('mousedown', onMouseDown, true); window.removeEventListener('mousemove', onMouseMove); window.removeEventListener('mouseup', onMouseUp); if (animFrameId) cancelAnimationFrame(animFrameId); };
  }, [data]);

  // Select-all + Ctrl+C with headers
  const isAllSelectedRef = useRef(false);
  useEffect(() => {
    const container = jRef.current; if (!container) return;
    const onSelectAllClick = (e: Event) => {
      const target = e.target as HTMLElement; if (!target.closest('td.jexcel_selectall')) return;
      const inst = spreadsheetRef.current; if (!inst) return;
      const gridData = inst.getData() as unknown[][]; if (gridData.length === 0) return;
      inst.updateSelectionFromCoords(0, 0, (gridData[0] as unknown[]).length - 1, gridData.length - 1);
      isAllSelectedRef.current = true;
    };
    const onCellClick = (e: Event) => { if (!(e.target as HTMLElement).closest('td.jexcel_selectall')) isAllSelectedRef.current = false; };
    const onKeyDown = async (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'c' && isAllSelectedRef.current) {
        e.preventDefault(); e.stopPropagation();
        const inst = spreadsheetRef.current; if (!inst) return;
        const currentOrder = columnOrderRef.current;
        const headers = currentOrder.map((origIdx) => COLUMNS[origIdx].title);
        const gridData = inst.getData() as CellValue[][];
        const lines: string[] = [headers.join('\t')];
        for (let r = 0; r < gridData.length; r++) lines.push(gridData[r].map((v) => String(v ?? '')).join('\t'));
        try { await navigator.clipboard.writeText(lines.join('\n')); } catch { /* ignore */ }
      }
    };
    // Intercept the browser "paste" event (Ctrl+V) before jspreadsheet's own
    // handler. When the clipboard matches what we last copied within this grid
    // (右クリック→コピー or Ctrl+C), paste using jspreadsheet's internal copy
    // data (inst.data) so cell colors (styles) are restored. clipboardData.getData
    // is synchronous so preventDefault() reliably stops the default paste.
    const onPaste = (e: ClipboardEvent) => {
      const inst = spreadsheetRef.current;
      if (!inst || lastCopiedTextRef.current === null) return;
      if (typeof inst.data !== 'string') return;
      const sel = (inst as any).selectedCell;
      if (!sel) return;
      const text = e.clipboardData?.getData('text') ?? '';
      if (text !== lastCopiedTextRef.current) return; // external clipboard
      e.preventDefault();
      e.stopPropagation();
      const x = Math.min(Number(sel[0]), Number(sel[2]));
      const y = Math.min(Number(sel[1]), Number(sel[3]));
      inst.paste(x, y, inst.data);
    };
    container.addEventListener('click', onSelectAllClick);
    container.addEventListener('mousedown', onCellClick);
    container.addEventListener('keydown', onKeyDown, true);
    container.addEventListener('paste', onPaste, true);
    return () => { container.removeEventListener('click', onSelectAllClick); container.removeEventListener('mousedown', onCellClick); container.removeEventListener('keydown', onKeyDown, true); container.removeEventListener('paste', onPaste, true); };
  }, [data]);

  // Column AutoFit
  useEffect(() => {
    const container = jRef.current; if (!container) return;
    const MIN_WIDTH = 60; const MAX_WIDTH = 600; const PADDING = 16;
    let canvasCtx: CanvasRenderingContext2D | null = null;
    function measureText(text: string, font: string): number {
      if (!canvasCtx) { const canvas = document.createElement('canvas'); canvasCtx = canvas.getContext('2d'); }
      if (!canvasCtx) return text.length * 8;
      canvasCtx.font = font; return canvasCtx.measureText(text).width;
    }
    function autoFitColumn(displayCol: number) {
      const inst = spreadsheetRef.current as any; if (!inst) return;
      const headerCell = inst.headers?.[displayCol] as HTMLElement | undefined;
      const headerFont = headerCell ? `${getComputedStyle(headerCell).fontWeight} ${getComputedStyle(headerCell).fontSize} ${getComputedStyle(headerCell).fontFamily}` : 'bold 13px sans-serif';
      const headerText = headerCell?.textContent || COLUMNS[columnOrderRef.current[displayCol]]?.title || '';
      let maxWidth = measureText(headerText, headerFont) + PADDING + 20;
      const gridData = inst.getData() as CellValue[][]; const rows = inst.rows as HTMLElement[];
      const firstVisibleCell = inst.records?.[0]?.[displayCol] as HTMLElement | undefined;
      const cellFont = firstVisibleCell ? `${getComputedStyle(firstVisibleCell).fontSize} ${getComputedStyle(firstVisibleCell).fontFamily}` : '13px sans-serif';
      for (let r = 0; r < gridData.length; r++) {
        if (rows[r] && rows[r].style.display === 'none') continue;
        const val = String(gridData[r]?.[displayCol] ?? ''); if (!val) continue;
        const w = measureText(val, cellFont) + PADDING; if (w > maxWidth) maxWidth = w;
      }
      inst.setWidth(displayCol, Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.ceil(maxWidth))));
      saveColumnStateToStorage(); updateFreezeOffsets();
    }
    function autoFitAllColumns() {
      const currentOrder = columnOrderRef.current; const hidden = hiddenColumnsRef.current;
      for (let d = 0; d < currentOrder.length; d++) { if (hidden.has(currentOrder[d])) continue; autoFitColumn(d); }
    }
    function onDblClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const headerCell = target.closest('thead td[data-x]') as HTMLElement | null; if (!headerCell) return;
      const rect = headerCell.getBoundingClientRect();
      if (rect.right - e.clientX > 8) return;
      const displayCol = Number(headerCell.getAttribute('data-x')); if (Number.isNaN(displayCol)) return;
      e.preventDefault(); e.stopPropagation();
      if (e.shiftKey) autoFitAllColumns(); else autoFitColumn(displayCol);
    }
    container.addEventListener('dblclick', onDblClick, true);
    return () => { container.removeEventListener('dblclick', onDblClick, true); };
  }, [data, saveColumnStateToStorage, updateFreezeOffsets]);

  // Link column click navigation
  useEffect(() => {
    const container = jRef.current;
    if (!container) return;
    function onClick(e: MouseEvent) {
      const target = e.target as HTMLElement;
      const cell = target.closest('td[data-x][data-y]') as HTMLElement | null;
      if (!cell) return;
      const displayCol = Number(cell.getAttribute('data-x'));
      const rowIdx = Number(cell.getAttribute('data-y'));
      if (Number.isNaN(displayCol) || Number.isNaN(rowIdx)) return;
      const origIdx = columnOrderRef.current[displayCol];
      if (!LINK_COLUMNS.includes(origIdx)) return;
      // Only navigate if the cell has content (icon displayed)
      const inst = spreadsheetRef.current;
      if (!inst) return;
      const cellValue = String(inst.getValueFromCoords(displayCol, rowIdx) ?? '').trim();
      if (!cellValue) return;
      // Get customer id and name
      const customerId = customerIdsRef.current[rowIdx];
      if (!customerId) return;
      const nameDisplayCol = columnOrderRef.current.indexOf(1); // Orig_Index 1 = 顧客名
      const customerName = nameDisplayCol !== -1
        ? String(inst.getValueFromCoords(nameDisplayCol, rowIdx) ?? '').trim()
        : '';
      const params = `?customerId=${customerId}&customerName=${encodeURIComponent(customerName)}`;
      if (origIdx === 8) navigate(`/assets${params}`);
      else if (origIdx === 9) navigate(`/contracts${params}`);
      else if (origIdx === 10) navigate(`/licenses${params}`);
      else if (origIdx === 11) navigate(`/customers/${customerId}/karte`);
      else if (origIdx === 12) navigate(`/customers/${customerId}/tickets`);
    }
    container.addEventListener('click', onClick);
    return () => { container.removeEventListener('click', onClick); };
  }, [data, navigate]);

  // Add row (toolbar)
  const handleAddRow = () => {
    if (!spreadsheetRef.current) return;
    // insertRow(numRows, rowIndex, insertBefore): passing a NUMBER (not a
    // data array) makes jspreadsheet fill every column with ''. Passing
    // `[emptyRow]` (an array wrapping the row array) instead corrupted
    // display column 0 with the emptyRow array itself, which produced a
    // misaligned outline/dropdown after adding a row.
    spreadsheetRef.current.insertRow(1, 0, true);
    updateCustomerIds((prev) => applyRowIdOperation(prev, { type: 'insert', position: 0, newIds: [`${NEW_ROW_ID_PREFIX}${Date.now()}`] }));
    updateUndoRedoButtons();
    // A freshly-created row's cells start fully visible — hideColumn() only
    // touches the rows that existed when it was called. Re-apply the hidden-
    // column set so the new row's cells line up with every other row.
    applyColumnVisibility();
    applyRequiredFieldHighlight();
  };

  // Build cellMeta for a row (only data columns 0-7, exclude link columns 8-10)
  const buildCellMeta = (rowIdx: number): string | null => {
    const inst = spreadsheetRef.current; if (!inst) return null;
    const rowCellMeta: CellMetaMap = {};
    const currentOrder = columnOrderRef.current;
    for (let displayIdx = 0; displayIdx < currentOrder.length; displayIdx++) {
      const origIdx = currentOrder[displayIdx];
      if (LINK_COLUMNS.includes(origIdx)) continue; // skip link columns
      const cellName = cellNameFromCoords(displayIdx, rowIdx);
      const color = extractBackgroundColor(inst.getStyle(cellName) as string);
      const comment = inst.getComments(cellName) as string;
      if (color) rowCellMeta[origIdx] = { ...(rowCellMeta[origIdx] || {}), color };
      if (comment) rowCellMeta[origIdx] = { ...(rowCellMeta[origIdx] || {}), comment };
    }
    return serializeCellMeta(rowCellMeta);
  };

  // 一括保存
  const handleSaveAll = async () => {
    if (!spreadsheetRef.current) return;
    const gridData = spreadsheetRef.current.getData() as CellValue[][];
    const currentOrder = columnOrderRef.current;
    let successCount = 0; let errorCount = 0;

    // DELETE
    const idsToDelete = Array.from(pendingDeleteIdsRef.current);
    let deleteSuccessCount = 0; let deleteErrorCount = 0;
    for (const id of idsToDelete) {
      try { await api.delete(`/customers/${id}`); deleteSuccessCount++; pendingDeleteIdsRef.current.delete(id); }
      catch { deleteErrorCount++; }
    }

    const rowIds = Array.from({ length: gridData.length }, (_, i) => customerIdsRef.current[i] ?? '');
    const plans = classifyDirtyRows(rowIds, dirtyContentRowIdsRef.current, dirtyMetaRowIdsRef.current);

    for (let i = 0; i < gridData.length; i++) {
      const plan = plans[i];
      if (!plan || plan.kind === 'skip') continue;

      const originalRow = displayToOriginalRow(gridData[i], currentOrder, COLUMNS.length) as CellValue[];
      const rowId = plan.rowId;
      const isNewRow = plan.kind === 'post';

      // 空行スキップ: 顧客コード（Orig_Index 0）が空の新規行はスキップ
      if (isNewRow && isBlankValue(originalRow[0])) continue;

      const payload = {
        customerCode: String(originalRow[0] ?? '').trim() || null,
        name: String(originalRow[1] ?? '').trim() || null,
        nameKana: String(originalRow[2] ?? '').trim() || null,
        contactName: String(originalRow[3] ?? '').trim() || null,
        phone: String(originalRow[4] ?? '').trim() || null,
        email: String(originalRow[5] ?? '').trim() || null,
        address: String(originalRow[6] ?? '').trim() || null,
        notes: String(originalRow[7] ?? '').trim() || null,
      };

      try {
        if (isNewRow) {
          await api.post('/customers', { ...payload, cellMeta: buildCellMeta(i) });
          successCount++;
        } else {
          if (plan.kind === 'put' || plan.kind === 'both') {
            await api.put(`/customers/${rowId}`, payload);
            dirtyContentRowIdsRef.current.delete(rowId);
          }
          if (plan.kind === 'patch' || plan.kind === 'both') {
            await api.patch(`/customers/${rowId}/cell-meta`, { cellMeta: buildCellMeta(i) });
            dirtyMetaRowIdsRef.current.delete(rowId);
          }
          successCount++;
        }
      } catch { errorCount++; }
    }

    queryClient.invalidateQueries({ queryKey: ['customers-spreadsheet'] });
    queryClient.invalidateQueries({ queryKey: ['customers'] });
    queryClient.invalidateQueries({ queryKey: ['customers-list'] });
    updateDirtyCounts();

    const totalErrorCount = errorCount + deleteErrorCount;
    const messages: string[] = [];
    if (successCount > 0 || errorCount > 0) messages.push(`保存: 成功${successCount}件${errorCount > 0 ? `、失敗${errorCount}件` : ''}`);
    if (deleteSuccessCount > 0 || deleteErrorCount > 0) messages.push(`削除: 成功${deleteSuccessCount}件${deleteErrorCount > 0 ? `、失敗${deleteErrorCount}件` : ''}`);
    if (messages.length === 0) showSuccess('変更はありませんでした');
    else if (totalErrorCount === 0) showSuccess(messages.join(' / '));
    else showError(messages.join(' / '));
  };

  // Context menu actions
  const handleContextCopy = async () => {
    restoreContextSelection();
    const inst = spreadsheetRef.current;
    if (!inst || !contextMenu) { setContextMenu(null); return; }
    // jspreadsheetの内部コピー処理を先に呼び、スタイル情報（セル色）を内部
    // バッファに記録させる。同一グリッド内への貼り付け時に色が復元される。
    inst.copy(true);
    const currentOrder = columnOrderRef.current; const hidden = hiddenColumnsRef.current;
    const [x1, y1, x2, y2] = contextMenu.range;
    const colStart = Math.min(x1, x2); const colEnd = Math.max(x1, x2);
    const rowStart = Math.min(y1, y2); const rowEnd = Math.max(y1, y2);
    const lines: string[] = [];
    for (let r = rowStart; r <= rowEnd; r++) {
      const cells: string[] = [];
      for (let c = colStart; c <= colEnd; c++) { const orig = currentOrder[c]; if (orig !== undefined && hidden.has(orig)) continue; cells.push(String(inst.getValueFromCoords(c, r) ?? '')); }
      lines.push(cells.join('\t'));
    }
    setContextMenu(null);
    const text = lines.join('\n');
    // 同一グリッド内貼り付けで色を復元するため、コピーしたテキストを控える。
    lastCopiedTextRef.current = text;
    try { await navigator.clipboard.writeText(text); } catch { showError('クリップボードへのコピーに失敗しました。'); }
  };

  const handleCopyAll = async () => {
    const inst = spreadsheetRef.current; if (!inst) { setContextMenu(null); return; }
    const currentOrder = columnOrderRef.current;
    const headers = currentOrder.map((origIdx) => COLUMNS[origIdx].title);
    const gridData = inst.getData() as CellValue[][];
    const lines: string[] = [headers.join('\t')];
    for (let r = 0; r < gridData.length; r++) lines.push(gridData[r].map((v) => String(v ?? '')).join('\t'));
    setContextMenu(null);
    try { await navigator.clipboard.writeText(lines.join('\n')); showSuccess('全データをコピーしました'); } catch { showError('クリップボードへのコピーに失敗しました。'); }
  };

  const handleContextPaste = async () => {
    const range = contextMenu?.range; setContextMenu(null);
    if (!range) return;
    try {
      const text = await navigator.clipboard.readText();
      if (!text) return;
      const inst = spreadsheetRef.current;
      if (!inst) return;
      // クリップボードの内容が直前の「右クリック→コピー」と一致する場合は、
      // jspreadsheet の内部コピーデータ（inst.data）を paste に渡して色も復元する。
      const useInternal = text === lastCopiedTextRef.current && typeof inst.data === 'string';
      inst.paste(range[0], range[1], useInternal ? inst.data : text);
    } catch { showError('クリップボードから貼り付けできませんでした。'); }
  };

  const handleSetCellColor = (color: string | null) => {
    const inst = spreadsheetRef.current;
    if (!inst || !contextMenu) return;
    const [x1, y1, x2, y2] = contextMenu.range;
    const colStart = Math.min(x1, x2); const colEnd = Math.max(x1, x2);
    const rowStart = Math.min(y1, y2); const rowEnd = Math.max(y1, y2);
    const targets: HTMLTableCellElement[] = [];
    for (let r = rowStart; r <= rowEnd; r++) { for (let c = colStart; c <= colEnd; c++) { const cell = inst.getCellFromCoords(c, r); if (cell) targets.push(cell); } }
    if (targets.length === 0) { setContextMenu(null); return; }
    if (color) {
      inst.setStyle(targets, 'background-color', color);
    } else {
      inst.setStyle(targets, 'background-color', 'transparent');
      targets.forEach((cell) => {
        cell.style.removeProperty('background-color');
        const colIdx = Number(cell.getAttribute('data-x')); const rowIdx = Number(cell.getAttribute('data-y'));
        const cellName = cellNameFromCoords(colIdx, rowIdx);
        const styleMap = (inst as any).options?.style ?? (inst as any).style;
        if (styleMap && styleMap[cellName]) {
          const cleaned = String(styleMap[cellName]).split(';').map((s: string) => s.trim()).filter((s: string) => s && !/^background-color\s*:/i.test(s)).join('; ');
          if (cleaned) styleMap[cellName] = cleaned; else delete styleMap[cellName];
        }
      });
    }
    for (let r = rowStart; r <= rowEnd; r++) markRowMetaDirtyByIndex(r);
    inst.updateSelectionFromCoords(x1, y1, x2, y2);
    setContextMenu(null);
    updateUndoRedoButtons();
    if (colorFilterSelectionsRef.current.size > 0) {
      applyColumnFilters();
    }
  };

  const handleOpenCommentDialog = () => {
    if (!contextMenu) return;
    const inst = spreadsheetRef.current;
    const cellName = cellNameFromCoords(contextMenu.col, contextMenu.row);
    const existing = (inst?.getComments(cellName) as string) || '';
    setCommentDialog({ col: contextMenu.col, row: contextMenu.row, value: existing });
    setContextMenu(null);
  };

  const handleSaveComment = () => {
    if (!commentDialog) return;
    const cellName = cellNameFromCoords(commentDialog.col, commentDialog.row);
    spreadsheetRef.current?.setComments(cellName, commentDialog.value.trim(), '');
    markRowMetaDirtyByIndex(commentDialog.row);
    updateDirtyCounts();
    setCommentDialog(null);
  };

  const handleClearComment = () => {
    if (!commentDialog) return;
    const cellName = cellNameFromCoords(commentDialog.col, commentDialog.row);
    spreadsheetRef.current?.setComments(cellName, '', '');
    markRowMetaDirtyByIndex(commentDialog.row);
    updateDirtyCounts();
    setCommentDialog(null);
  };

  // Undo / Redo
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);

  const updateUndoRedoButtons = useCallback(() => {
    const inst = spreadsheetRef.current as any;
    if (!inst) { setCanUndo(false); setCanRedo(false); return; }
    const history: unknown[] = inst.history ?? [];
    const historyIndex: number = inst.historyIndex ?? -1;
    setCanUndo(historyIndex >= 0);
    setCanRedo(historyIndex < history.length - 1);
    updateDirtyCounts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUndo = () => { spreadsheetRef.current?.undo(); updateUndoRedoButtons(); };
  const handleRedo = () => { spreadsheetRef.current?.redo(); updateUndoRedoButtons(); };

  const handleToggleFilter = () => {
    setShowFilters((prev) => {
      const next = !prev;
      if (!next && searchText) { setSearchText(''); spreadsheetRef.current?.resetSearch(); }
      return next;
    });
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value; setSearchText(value);
    if (spreadsheetRef.current) { if (value.trim()) spreadsheetRef.current.search(value); else spreadsheetRef.current.resetSearch(); }
  };

  const handleTemplateDownload = async () => {
    try {
      const res = await api.get('/customers/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a'); link.href = url;
      link.setAttribute('download', 'customer_template.csv');
      document.body.appendChild(link); link.click(); link.remove();
      window.URL.revokeObjectURL(url);
    } catch { showError('テンプレートのダウンロードに失敗しました'); }
  };

  const handleImportChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append('file', file);
    try {
      const res = await api.post('/customers/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { successCount, errorCount } = res.data;
      if (errorCount > 0) showError(`インポート完了: 成功${successCount}件, 失敗${errorCount}件`);
      else showSuccess(`インポート完了: 成功${successCount}件`);
      queryClient.invalidateQueries({ queryKey: ['customers-spreadsheet'] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    } catch { showError('CSVインポートに失敗しました'); }
    event.target.value = '';
  };

  // Unsaved changes guard
  useEffect(() => {
    if (!isDirty) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isDirty]);

  const [navDialogOpen, setNavDialogOpen] = useState(false);
  const pendingNavigationRef = useRef<string | null>(null);
  const navResolverRef = useRef<((allowed: boolean) => void) | null>(null);

  const { registerGuard } = useNavigationGuard();
  useEffect(() => {
    const unregister = registerGuard((_targetPath) => {
      const hasDirty = dirtyContentRowIdsRef.current.size > 0 || dirtyMetaRowIdsRef.current.size > 0 || pendingDeleteIdsRef.current.size > 0 || customerIdsRef.current.some((id) => id.startsWith(NEW_ROW_ID_PREFIX));
      if (!hasDirty) return true;
      pendingNavigationRef.current = _targetPath;
      setNavDialogOpen(true);
      return new Promise<boolean>((resolve) => { navResolverRef.current = resolve; });
    });
    return unregister;
  }, [registerGuard]);

  const handleNavSaveAndLeave = async () => { setNavDialogOpen(false); await handleSaveAll(); navResolverRef.current?.(true); navResolverRef.current = null; };
  const handleNavDiscardAndLeave = () => { setNavDialogOpen(false); dirtyContentRowIdsRef.current.clear(); dirtyMetaRowIdsRef.current.clear(); pendingDeleteIdsRef.current.clear(); setDirtyCounts({ modified: 0, added: 0, deleted: 0 }); navResolverRef.current?.(true); navResolverRef.current = null; };
  const handleNavCancel = () => { setNavDialogOpen(false); navResolverRef.current?.(false); navResolverRef.current = null; };

  if (isLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error" sx={{ m: 2 }}>顧客データの取得に失敗しました。</Alert>;

  return (
    <>
    <GlobalStyles styles={{}} />
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      height: 'calc(100vh - 48px - 24px)',
      mb: -3,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{ flexShrink: 0, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold' }}>顧客管理{isDirty ? ' *' : ''}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {isAdmin && (
              <>
                <Tooltip title="元に戻す (Ctrl+Z)">
                  <span>
                  <IconButton size="small" onClick={handleUndo} disabled={!canUndo} color={canUndo ? 'primary' : 'default'}>
                    <UndoIcon fontSize="small" />
                  </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="やり直し (Ctrl+Y)">
                  <span>
                  <IconButton size="small" onClick={handleRedo} disabled={!canRedo} color={canRedo ? 'primary' : 'default'}>
                    <RedoIcon fontSize="small" />
                  </IconButton>
                  </span>
                </Tooltip>
              </>
            )}
            <Button size="small" variant={showFilters ? 'contained' : 'outlined'} startIcon={<FilterListIcon />} onClick={handleToggleFilter} color={showFilters ? 'primary' : 'inherit'}>
              フィルター
            </Button>
            <Button size="small" variant={hiddenColumns.size > 0 ? 'contained' : 'outlined'} startIcon={<ViewColumnIcon />} onClick={() => setColumnVisibilityDialogOpen(true)} color={hiddenColumns.size > 0 ? 'primary' : 'inherit'}>
              表示列{hiddenColumns.size > 0 ? `（${COLUMNS.length - hiddenColumns.size}/${COLUMNS.length}）` : ''}
            </Button>
            {isAdmin && (
              <>
                <Button size="small" variant="outlined" startIcon={<DownloadIcon />} onClick={handleTemplateDownload}>テンプレート</Button>
                <Button size="small" variant="outlined" component="label" startIcon={<UploadFileIcon />}>
                  CSVインポート
                  <input type="file" accept=".csv" hidden onChange={handleImportChange} />
                </Button>
                <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={handleAddRow}>行追加</Button>
                <Button size="small" variant="contained" startIcon={<SaveIcon />} onClick={handleSaveAll}>一括保存</Button>
              </>
            )}
          </Box>
        </Box>
        {showFilters && (
          <TextField
            value={searchText}
            onChange={handleSearchChange}
            placeholder="顧客コード、顧客名、担当者名、電話番号、メールアドレスで検索..."
            size="small"
            fullWidth
            type="search"
            autoComplete="off"
            inputProps={{ autoComplete: 'off', name: 'search_filter_nocomplete' }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon color="action" /></InputAdornment>) }}
          />
        )}
      </Box>

      {/* Spreadsheet */}
      <Box
        onContextMenu={(e) => {
          e.preventDefault();
          const target = e.target as HTMLElement;
          const cell = target.closest('td');
          let col = 0; let row = 0;
          if (cell && cell.dataset) { col = parseInt(cell.dataset.x || '0', 10); row = parseInt(cell.dataset.y || '0', 10); }
          const isHeader = !!target.closest('thead');
          const inst = spreadsheetRef.current;
          const highlighted = inst?.highlighted as HTMLTableCellElement[] | undefined;
          const isPartOfSelection = !!highlighted?.some((el) => el === cell);
          const hasRangeSelection = isPartOfSelection && (highlighted?.length ?? 0) > 1;
          let range: [number, number, number, number] = [col, row, col, row];
          if (isPartOfSelection && inst?.selectedCell) {
            const [x1, y1, x2, y2] = inst.selectedCell as [number, number, number, number];
            range = [Number(x1), Number(y1), Number(x2), Number(y2)];
          } else { inst?.updateSelectionFromCoords(col, row, col, row); }
          setContextMenu({ mouseX: e.clientX, mouseY: e.clientY, col, row, hasRangeSelection, range, isHeader });
        }}
        sx={{
          flexGrow: 1,
          minHeight: 0,
          overflow: 'hidden',
          '& .jexcel_content': { overflow: 'auto !important', width: '100% !important' },
          '& .jexcel': { height: '100%' },
          '& .jexcel thead td.hidden-col-boundary': { borderLeft: '3px double #666 !important' },
          '& .jexcel thead td[data-x]': { cursor: 'default' },
          ...(showFilters && {
            '& .jexcel thead td[data-x]': { position: 'relative', paddingRight: '20px !important' },
            '& .jexcel thead td[data-x]::after': { content: '"▼"', position: 'absolute', right: '6px', top: '50%', transform: 'translateY(-50%)', fontSize: '9px', color: '#888', cursor: 'pointer' },
            '& .jexcel thead td[data-x].column-filter-active::after': { color: '#1976d2' },
          }),
          '& .jexcel tbody td.editor[data-x]': { zIndex: '20001 !important', overflow: 'visible !important' },
          '& .jdropdown-container': { backgroundColor: '#fff !important', zIndex: '20000 !important' },
          '& .jdropdown-content': { backgroundColor: '#fff !important', maxHeight: '400px !important', overflowY: 'auto !important', border: '1px solid #ccc !important', borderRadius: '4px', boxShadow: '0 4px 12px rgba(0,0,0,0.15) !important', zIndex: '20000 !important' },
          '& .jdropdown-close': { display: 'block !important', backgroundColor: '#1976d2 !important', color: '#fff !important', textAlign: 'center !important', padding: '6px 12px !important', cursor: 'pointer', fontWeight: 'bold' },
          '& .jdropdown-item': { padding: '6px 12px !important', borderBottom: '1px solid #f0f0f0 !important', cursor: 'pointer', '&:hover': { backgroundColor: '#e3f2fd !important' } },
          '& .jdropdown-item.jdropdown-selected': { backgroundColor: '#bbdefb !important', color: '#000 !important' },
          '& .jdropdown-default .jdropdown-item:hover': { color: '#000 !important' },
          '& .jexcel_search': { display: 'none !important' },
          // Link icon columns cursor
          '& .jexcel tbody td.customer-link-cell': {
            cursor: 'pointer',
            '&:hover': { backgroundColor: '#e3f2fd !important' },
          },
          // Freeze: 1 fixed column only (顧客コード = data-x="0")
          '& .jexcel thead > tr:not(.jexcel_filter) > td': { position: 'sticky !important', top: '0 !important', textAlign: 'center !important', borderBottom: 'none !important', boxShadow: '0 1px 0 0 #ccc' },
          '& .jexcel thead td.jexcel_selectall': { position: 'sticky !important', top: '0 !important', left: '0 !important', zIndex: '13 !important', backgroundColor: '#f3f3f3 !important' },
          '& .jexcel thead td[data-x="0"]': { position: 'sticky !important', top: '0 !important', left: 'var(--freeze-offset-col0, 0px) !important', zIndex: '12 !important', backgroundColor: '#f3f3f3 !important', boxShadow: '2px 0 2px 0 rgba(0,0,0,0.15)' },
          '& .jexcel tbody td.jexcel_row': { position: 'sticky !important', left: '0 !important', zIndex: '12 !important', backgroundColor: '#f3f3f3 !important', borderRight: 'none !important', boxShadow: '1px 0 0 0 #ccc' },
          '& .jexcel tbody td[data-x="0"]': { position: 'sticky !important', left: 'var(--freeze-offset-col0, 0px) !important', zIndex: '11 !important', backgroundColor: '#fff', boxShadow: '2px 0 2px 0 rgba(0,0,0,0.15)' },
          // Ghost prevention: opaque color for highlight on frozen column
          '& .jexcel tbody td.highlight[data-x="0"]': { backgroundColor: '#e9f6f0' },
          '& .jexcel tbody td.highlight.jexcel_row': { backgroundColor: '#f3f3f3' },
          // Excel-like selection styling
          '& .jexcel tbody td.highlight': { backgroundColor: 'rgba(33, 163, 102, 0.10)' },
          '& .jexcel tbody td.highlight-left': { boxShadow: 'inset 2px 0 0 0 #21A366' },
          '& .jexcel tbody td.highlight-right': { boxShadow: 'inset -2px 0 0 0 #21A366' },
          '& .jexcel tbody td.highlight-top': { boxShadow: 'inset 0 2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-bottom': { boxShadow: 'inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-right': { boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366' },
          '& .jexcel tbody td.highlight-top.highlight-bottom': { boxShadow: 'inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-top': { boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-right.highlight-top': { boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-bottom': { boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-right.highlight-bottom': { boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-top': { boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-bottom': { boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-top.highlight-bottom': { boxShadow: 'inset 2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-right.highlight-top.highlight-bottom': { boxShadow: 'inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-left.highlight-right.highlight-top.highlight-bottom': { boxShadow: 'inset 2px 0 0 0 #21A366, inset -2px 0 0 0 #21A366, inset 0 2px 0 0 #21A366, inset 0 -2px 0 0 #21A366' },
          '& .jexcel tbody td.highlight-selected': { backgroundColor: 'rgba(33, 163, 102, 0.04)' },
        }}
        onClickCapture={(e) => {
          const target = e.target as HTMLElement;
          const headerCell = showFilters ? (target.closest('thead td[data-x]') as HTMLElement | null) : null;
          if (headerCell) {
            const rect = headerCell.getBoundingClientRect();
            const clickX = (e as unknown as React.MouseEvent).clientX;
            if (rect.right - clickX <= 22) { e.stopPropagation(); e.preventDefault(); openFilterDialog(Number(headerCell.getAttribute('data-x'))); }
          }
        }}
      >
        <div ref={jRef} style={{ width: '100%', height: '100%' }} />
      </Box>

      {/* Status bar */}
      <Box sx={{ flexShrink: 0, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2, px: 1.5, borderTop: '1px solid #e0e0e0', backgroundColor: '#f5f5f5', fontSize: '12px', color: '#555', userSelect: 'none' }}>
        {isDirty && (
          <Box sx={{ display: 'flex', gap: 1.5, color: '#d32f2f', fontWeight: 500 }}>
            {dirtyCounts.modified > 0 && <span>修正: {dirtyCounts.modified}件</span>}
            {dirtyCounts.added > 0 && <span>追加: {dirtyCounts.added}件</span>}
            {dirtyCounts.deleted > 0 && <span>削除: {dirtyCounts.deleted}件</span>}
          </Box>
        )}
        <Box sx={{ flexGrow: 1 }} />
        {statusBar && (
          <>
            <span>選択セル数: {statusBar.count}</span>
            {statusBar.numericCount > 0 && (
              <>
                <span>数値セル数: {statusBar.numericCount}</span>
                <span>合計: {statusBar.sum.toLocaleString()}</span>
                <span>平均: {statusBar.avg.toLocaleString(undefined, { maximumFractionDigits: 2 })}</span>
                <span>最小: {statusBar.min.toLocaleString()}</span>
                <span>最大: {statusBar.max.toLocaleString()}</span>
              </>
            )}
          </>
        )}
      </Box>

      {/* Context menu */}
      <Menu open={contextMenu !== null} onClose={() => setContextMenu(null)} anchorReference="anchorPosition" anchorPosition={contextMenu ? { top: contextMenu.mouseY, left: contextMenu.mouseX } : undefined}>
        <MuiMenuItem onClick={handleContextCopy}><ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon><ListItemText>コピー</ListItemText></MuiMenuItem>
        <MuiMenuItem onClick={handleCopyAll}><ListItemIcon><CopyIcon fontSize="small" /></ListItemIcon><ListItemText>すべてコピー（ヘッダー付き）</ListItemText></MuiMenuItem>
        {isAdmin && (<MuiMenuItem onClick={handleContextPaste}><ListItemIcon><PasteIcon fontSize="small" /></ListItemIcon><ListItemText>貼り付け</ListItemText></MuiMenuItem>)}
        <Divider />
        {/* Move column left: only from display position 2+ (can't move into frozen col 0) */}
        {isAdmin && contextMenu && contextMenu.col > 1 && (
          <MuiMenuItem onClick={() => { spreadsheetRef.current?.moveColumn(contextMenu.col, contextMenu.col - 1); saveColumnStateToStorage(); setContextMenu(null); }}>
            <ListItemIcon><ArrowLeftIcon fontSize="small" /></ListItemIcon><ListItemText>列を左に移動</ListItemText>
          </MuiMenuItem>
        )}
        {isAdmin && contextMenu && contextMenu.col >= 1 && contextMenu.col < COLUMNS.length - 1 && (
          <MuiMenuItem onClick={() => { spreadsheetRef.current?.moveColumn(contextMenu.col, contextMenu.col + 1); saveColumnStateToStorage(); setContextMenu(null); }}>
            <ListItemIcon><ArrowRightIcon fontSize="small" /></ListItemIcon><ListItemText>列を右に移動</ListItemText>
          </MuiMenuItem>
        )}
        <Divider />
        {contextMenu && (
          <MuiMenuItem onClick={() => {
            const currentOrder = columnOrderRef.current;
            const [x1, , x2] = contextMenu.range;
            const from = Math.min(x1, x2); const to = Math.max(x1, x2);
            const targets: number[] = [];
            for (let d = from; d <= to; d++) { const orig = currentOrder[d]; if (orig === undefined) continue; if (d === 0) continue; targets.push(orig); }
            if (targets.length > 0) hideColumnsByOrigIdx(targets);
            setContextMenu(null);
          }} disabled={contextMenu.col === 0}>
            <ListItemIcon><VisibilityOffIcon fontSize="small" /></ListItemIcon><ListItemText>列を非表示</ListItemText>
          </MuiMenuItem>
        )}
        {contextMenu && getAdjacentHiddenOrigIdxs(contextMenu.col).length > 0 && (
          <MuiMenuItem onClick={() => { showColumnsByOrigIdx(getAdjacentHiddenOrigIdxs(contextMenu.col)); setContextMenu(null); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon><ListItemText>列を再表示</ListItemText>
          </MuiMenuItem>
        )}
        {hiddenColumns.size > 0 && (
          <MuiMenuItem onClick={() => { showAllColumns(); setContextMenu(null); }}>
            <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon><ListItemText>すべての列を再表示</ListItemText>
          </MuiMenuItem>
        )}
        {isAdmin && <Divider />}
        {isAdmin && (
          <MuiMenuItem onClick={() => {
            const row = contextMenu?.row ?? 0;
            spreadsheetRef.current?.insertRow(1, row);
            updateCustomerIds((prev) => applyRowIdOperation(prev, { type: 'insert', position: row + 1, newIds: [`${NEW_ROW_ID_PREFIX}${Date.now()}`] }));
            setContextMenu(null); updateUndoRedoButtons(); applyColumnVisibility(); applyRequiredFieldHighlight(); applyLinkCellStyles();
          }}><ListItemIcon><InsertRowIcon fontSize="small" /></ListItemIcon><ListItemText>行を追加（選択行の下）</ListItemText></MuiMenuItem>
        )}
        {isAdmin && (
          <MuiMenuItem onClick={() => { spreadsheetRef.current?.deleteRow(contextMenu?.row); setContextMenu(null); }}>
            <ListItemIcon><DeleteRowIcon fontSize="small" /></ListItemIcon><ListItemText>行を削除</ListItemText>
          </MuiMenuItem>
        )}
        {/* MUI の Menu は子要素をクローンしてキーボード操作用の props を注入するため、
            Fragment を子に渡すと "The Menu component doesn't accept a Fragment as
            a child" の警告になる。条件付きのグループは <> で包まず、key 付き要素の
            配列として返す。 */}
        {isAdmin && [
          <Divider key="comment-divider" />,
          <MuiMenuItem key="comment" onClick={handleOpenCommentDialog}><ListItemIcon><CommentIcon fontSize="small" /></ListItemIcon><ListItemText>コメント編集</ListItemText></MuiMenuItem>,
          <Divider key="color-divider" />,
          <Box key="color-label" sx={{ px: 2, py: 0.5, display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
            <PaletteIcon fontSize="small" /><Typography variant="caption">セル色</Typography>
          </Box>,
          <Box key="color-swatches" sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 22px)', gap: 0.75, px: 2, py: 0.5, justifyContent: 'start' }}>
            {CELL_COLOR_PALETTE.map((c) => (
              <Tooltip key={c.value} title={c.label}>
                <Box onClick={() => handleSetCellColor(c.value)} sx={{ width: 22, height: 22, borderRadius: '50%', backgroundColor: c.value, border: '1px solid #999', cursor: 'pointer', '&:hover': { transform: 'scale(1.15)' } }} />
              </Tooltip>
            ))}
          </Box>,
          <MuiMenuItem key="color-clear" onClick={() => handleSetCellColor(null)}><ListItemIcon><ClearColorIcon fontSize="small" /></ListItemIcon><ListItemText>色をクリア</ListItemText></MuiMenuItem>,
        ]}
      </Menu>

      {/* Navigation guard dialog */}
      <Dialog open={navDialogOpen} onClose={handleNavCancel} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <WarningIcon sx={{ color: '#ed6c02', fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>変更内容が保存されていません</Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, p: 2 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>変更内容</Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {dirtyCounts.modified > 0 && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EditIcon sx={{ fontSize: 18, color: '#1976d2' }} /><Typography variant="body2">修正 {dirtyCounts.modified}件</Typography></Box>)}
              {dirtyCounts.added > 0 && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><AddCircleIcon sx={{ fontSize: 18, color: '#2e7d32' }} /><Typography variant="body2">追加 {dirtyCounts.added}件</Typography></Box>)}
              {dirtyCounts.deleted > 0 && (<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><DeleteIcon2 sx={{ fontSize: 18, color: '#d32f2f' }} /><Typography variant="body2">削除 {dirtyCounts.deleted}件</Typography></Box>)}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleNavCancel} variant="outlined">キャンセル</Button>
          <Button onClick={handleNavDiscardAndLeave} variant="outlined">保存せずに移動</Button>
          <Button onClick={handleNavSaveAndLeave} variant="contained" startIcon={<SaveIcon />}>保存して移動</Button>
        </DialogActions>
      </Dialog>

      {/* Column visibility dialog */}
      <Dialog open={columnVisibilityDialogOpen} onClose={() => setColumnVisibilityDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>表示列の設定</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', gap: 1, mb: 1 }}><Button size="small" onClick={showAllColumns}>すべて表示</Button></Box>
          <Divider sx={{ mb: 1 }} />
          <FormGroup>
            {columnOrderRef.current.map((origIdx) => {
              const isFrozen = FIXED_COLUMNS.includes(origIdx);
              return (
                <FormControlLabel key={origIdx} control={<Checkbox size="small" checked={isFrozen || !hiddenColumns.has(origIdx)} onChange={() => toggleColumnVisibility(origIdx)} disabled={isFrozen} />}
                  label={<Typography variant="body2" component="span">{COLUMNS[origIdx].title}{isFrozen && (<Typography variant="caption" color="text.secondary" component="span" sx={{ ml: 0.5 }}>(固定)</Typography>)}</Typography>}
                />
              );
            })}
          </FormGroup>
        </DialogContent>
        <DialogActions><Button onClick={() => setColumnVisibilityDialogOpen(false)} variant="contained">閉じる</Button></DialogActions>
      </Dialog>

      {/* Comment editor dialog */}
      <Dialog open={commentDialog !== null} onClose={() => setCommentDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>コメント編集</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth multiline minRows={3} value={commentDialog?.value ?? ''} onChange={(e) => setCommentDialog((prev) => (prev ? { ...prev, value: e.target.value } : prev))} placeholder="コメントを入力..." />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClearComment} color="inherit">クリア</Button>
          <Button onClick={() => setCommentDialog(null)}>キャンセル</Button>
          <Button onClick={handleSaveComment} variant="contained">保存</Button>
        </DialogActions>
      </Dialog>

      {/* Column filter dialog */}
      <Dialog open={filterDialog !== null} onClose={handleFilterDialogCancel} maxWidth="xs" fullWidth>
        <DialogTitle>{filterDialog?.title} のフィルター</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <TextField autoFocus size="small" fullWidth placeholder="候補を検索..." value={filterDialog?.search ?? ''} onChange={(e) => handleFilterDialogSearchChange(e.target.value)} autoComplete="new-password" inputProps={{ autoComplete: 'new-password', 'data-lpignore': 'true', 'data-form-type': 'other' }} InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>) }} />
          <Box sx={{ display: 'flex', gap: 1 }}><Button size="small" onClick={handleFilterDialogSelectAll}>全選択</Button><Button size="small" onClick={handleFilterDialogDeselectAll}>全解除</Button></Box>
          <Divider />
          <Box sx={{ maxHeight: 320, overflowY: 'auto', border: '1px solid #eee', borderRadius: 1 }}>
            <FormGroup sx={{ px: 1 }}>
              {filterDialog?.allValues.filter((v) => !filterDialog.search.trim() || v.toLowerCase().includes(filterDialog.search.trim().toLowerCase())).map((value) => (
                <FormControlLabel key={value} control={<Checkbox size="small" checked={filterDialog.selected.has(value)} onChange={() => handleFilterDialogToggleValue(value)} />} label={value === '' ? '(空白)' : value} />
              ))}
              {filterDialog && filterDialog.allValues.filter((v) => !filterDialog.search.trim() || v.toLowerCase().includes(filterDialog.search.trim().toLowerCase())).length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ p: 1 }}>該当する候補がありません</Typography>
              )}
            </FormGroup>
          </Box>
          {/* Color filter section */}
          {filterDialog && filterDialog.allColors.length > 0 && (
            <>
              <Divider sx={{ mt: 1 }} />
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>色でフィルター</Typography>
              <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
                <Button size="small" onClick={() => setFilterDialog((prev) => prev ? { ...prev, selectedColors: new Set(prev.allColors) } : prev)}>全選択</Button>
                <Button size="small" onClick={() => setFilterDialog((prev) => prev ? { ...prev, selectedColors: new Set() } : prev)}>全解除</Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {filterDialog.allColors.map((color) => {
                  const isNone = color === '__none__';
                  const paletteEntry = CELL_COLOR_PALETTE.find((p) => p.value.toLowerCase() === color);
                  const label = isNone ? '色なし' : '';
                  const checked = filterDialog.selectedColors.has(color);
                  return (
                    <Box
                      key={color}
                      onClick={() => setFilterDialog((prev) => {
                        if (!prev) return prev;
                        const next = new Set(prev.selectedColors);
                        if (next.has(color)) next.delete(color); else next.add(color);
                        return { ...prev, selectedColors: next };
                      })}
                      sx={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                        px: 1, py: 0.5, borderRadius: 1, cursor: 'pointer',
                        minHeight: 28, minWidth: 28,
                        border: checked ? '2px solid #1976d2' : '1px solid #ccc',
                        backgroundColor: isNone ? '#fff' : color,
                        opacity: checked ? 1 : 0.5,
                        '&:hover': { opacity: 1 },
                      }}
                    >
                      <Typography variant="caption" sx={{ color: isNone ? 'text.primary' : 'inherit', fontWeight: checked ? 'bold' : 'normal' }}>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={handleFilterDialogCancel}>キャンセル</Button><Button onClick={handleFilterDialogOk} variant="contained">OK</Button></DialogActions>
      </Dialog>
    </Box>
    </>
  );
};

export default CustomerSpreadsheetPage;
